// /forge/interventions — Retention intervention coordinator UI.
//
// Shows ranked candidates produced by the nightly churn + uplift models and
// lets the coordinator record a delivered/skipped intervention in one click.
// Candidates in the uplift-calibration control slice are displayed in a
// separate section with an explicit "do not nudge" badge so the coordinator
// knows the system is holding them out on purpose (§2.5 of the research doc).
//
// Research: docs/research/2026-04-19-dcvfd-ai-ml-capability-research-q2-q4-2026.md §2.4
// Plan:     docs/plans/2026-04-19-ai-ml-execution-plan.md Unit 0

import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";

interface Candidate {
  id: number;
  generated_at: string;
  run_id: string;
  member_id: number;
  rank: number;
  risk_score: number;
  uplift_score: number | null;
  composite_score: number;
  recommended_type: string;
  reasoning: string | null;
  is_control: number;
  member_name?: string;
  member_status?: string;
  joined_date?: string;
}

interface InterventionRow {
  id: number;
  member_id: number;
  triggered_at: string;
  triggered_by: string;
  intervention_type: string;
  delivered_at: string | null;
  delivered_by: string | null;
  notes: string | null;
  outcome_30d: string | null;
  outcome_60d: string | null;
  outcome_90d: string | null;
  model_version_at_trigger: string;
  is_control: number;
}

const TYPE_LABELS: Record<string, string> = {
  check_in_call: "Check-in call",
  sms_outreach: "SMS outreach",
  shift_offer: "Shift offer",
  mentor_assignment: "Mentor assignment",
  no_action: "No action",
};

export default function Interventions() {
  const { user } = useAuth();
  const canView = user?.role === "board_member" || user?.role === "admin";

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [controlCandidates, setControlCandidates] = useState<Candidate[]>([]);
  const [history, setHistory] = useState<InterventionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [candRes, histRes] = await Promise.all([
        fetch("/api/ai/interventions/candidates?include_control=1", {
          credentials: "same-origin",
        }),
        fetch("/api/ai/interventions?limit=50", { credentials: "same-origin" }),
      ]);
      if (!candRes.ok || !histRes.ok) {
        throw new Error("Failed to load intervention data");
      }
      const candJson = (await candRes.json()) as { candidates: Candidate[] };
      const histJson = (await histRes.json()) as {
        interventions: InterventionRow[];
      };
      const all = candJson.candidates ?? [];
      setCandidates(all.filter((c) => c.is_control === 0));
      setControlCandidates(all.filter((c) => c.is_control === 1));
      setHistory(histJson.interventions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const record = useCallback(
    async (c: Candidate, delivered: boolean) => {
      setSubmitting(c.id);
      try {
        const res = await fetch("/api/ai/interventions", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            member_id: c.member_id,
            triggered_by: "model_auto",
            risk_score_at_trigger: c.risk_score,
            uplift_score_at_trigger: c.uplift_score,
            intervention_type: c.recommended_type,
            delivered_at: delivered ? new Date().toISOString() : null,
            notes: c.reasoning,
            model_version_at_trigger: c.run_id,
            is_control: false,
          }),
        });
        if (!res.ok) throw new Error("record failed");
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Submit failed");
      } finally {
        setSubmitting(null);
      }
    },
    [load],
  );

  if (!canView) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        This page is restricted to coordinators (board members and admins).
      </div>
    );
  }

  if (loading) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        Loading intervention candidates…
      </div>
    );
  }

  const hasAnyCandidates = candidates.length + controlCandidates.length > 0;

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <header>
        <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
          FORGE · Retention
        </p>
        <h1 className="text-2xl font-bold text-theme-primary tracking-tight">
          Intervention Coordinator
        </h1>
        <p className="text-sm text-theme-secondary mt-1">
          Ranked candidates from the nightly churn model. Record what you did —
          the system needs the outcome to learn whether the nudge worked.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 text-red-900 text-sm px-4 py-2">
          {error}
        </div>
      )}

      {!hasAnyCandidates && (
        <div className="glass rounded-xl p-6 text-sm text-theme-muted">
          No candidates yet — the nightly churn model has not populated{" "}
          <code className="font-mono">dash_ai_intervention_candidates</code>.
          This table will populate after Unit 1 + Unit 2 (survival + classifier)
          ships.
        </div>
      )}

      {candidates.length > 0 && (
        <section className="glass rounded-2xl p-5 md:p-6">
          <h2 className="text-lg font-bold text-theme-primary mb-3">
            Active candidates ({candidates.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 px-3">Member</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Risk</th>
                  <th className="py-2 px-3 text-right">Uplift</th>
                  <th className="py-2 px-3">Recommended</th>
                  <th className="py-2 px-3">Reasoning</th>
                  <th className="py-2 pl-3 text-right">Record</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-2 pr-3 text-theme-muted tabular-nums">
                      {c.rank}
                    </td>
                    <td className="py-2 px-3 text-theme-primary">
                      {c.member_name && c.member_name.length > 0 ? (
                        <span>
                          {c.member_name}
                          <span className="ml-2 text-[10px] font-mono text-theme-muted">
                            #{c.member_id}
                          </span>
                        </span>
                      ) : (
                        <span className="font-mono text-xs">{c.member_id}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-theme-muted">
                      {c.member_status || "—"}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-mono">
                      {c.risk_score.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-mono">
                      {c.uplift_score === null
                        ? "—"
                        : c.uplift_score.toFixed(3)}
                    </td>
                    <td className="py-2 px-3 text-theme-primary">
                      {TYPE_LABELS[c.recommended_type] ?? c.recommended_type}
                    </td>
                    <td className="py-2 px-3 text-xs text-theme-muted max-w-xs truncate">
                      {c.reasoning ?? "—"}
                    </td>
                    <td className="py-2 pl-3 text-right space-x-2 whitespace-nowrap">
                      <button
                        className="px-2 py-1 text-xs rounded bg-emerald-500/80 text-white hover:bg-emerald-600 disabled:opacity-50"
                        disabled={submitting === c.id}
                        onClick={() => void record(c, true)}
                      >
                        Delivered
                      </button>
                      <button
                        className="px-2 py-1 text-xs rounded border border-theme-subtle text-theme-secondary hover:bg-theme-overlay disabled:opacity-50"
                        disabled={submitting === c.id}
                        onClick={() => void record(c, false)}
                      >
                        Skip
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {controlCandidates.length > 0 && (
        <section className="glass rounded-2xl p-5 md:p-6 border-l-4 border-amber-400">
          <h2 className="text-lg font-bold text-theme-primary mb-1">
            Uplift control holdout ({controlCandidates.length})
          </h2>
          <p className="text-xs text-amber-700 mb-3">
            The system is <strong>not</strong> auto-suggesting these members so
            it can measure whether its nudges work. You can still reach out if
            you independently decide to — just don't let the list drive the
            decision.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                  <th className="py-2 pr-3">Member</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Risk</th>
                  <th className="py-2 px-3 text-right">Uplift</th>
                  <th className="py-2 px-3">Would-have recommended</th>
                  <th className="py-2 px-3">Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {controlCandidates.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-2 pr-3 text-theme-primary">
                      {c.member_name && c.member_name.length > 0 ? (
                        <span>
                          {c.member_name}
                          <span className="ml-2 text-[10px] font-mono text-theme-muted">
                            #{c.member_id}
                          </span>
                        </span>
                      ) : (
                        <span className="font-mono text-xs">{c.member_id}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-theme-muted">
                      {c.member_status || "—"}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-mono">
                      {c.risk_score.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-mono">
                      {c.uplift_score === null
                        ? "—"
                        : c.uplift_score.toFixed(3)}
                    </td>
                    <td className="py-2 px-3 text-theme-secondary">
                      {TYPE_LABELS[c.recommended_type] ?? c.recommended_type}
                    </td>
                    <td className="py-2 px-3 text-xs text-theme-muted max-w-xs truncate">
                      {c.reasoning ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="glass rounded-2xl p-5 md:p-6">
        <h2 className="text-lg font-bold text-theme-primary mb-3">
          Recent interventions ({history.length})
        </h2>
        {history.length === 0 ? (
          <p className="text-sm text-theme-muted">
            No interventions recorded yet. Once you deliver an outreach, the
            30/60/90-day outcome auto-populates from status-change data.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 px-3">Member</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Delivered</th>
                  <th className="py-2 px-3">By</th>
                  <th className="py-2 px-3">30d</th>
                  <th className="py-2 px-3">60d</th>
                  <th className="py-2 pl-3">90d</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr
                    key={h.id}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="py-2 pr-3 text-theme-muted font-mono text-xs">
                      {h.triggered_at.slice(0, 10)}
                    </td>
                    <td className="py-2 px-3 font-mono text-theme-primary">
                      {h.member_id}
                    </td>
                    <td className="py-2 px-3 text-theme-secondary">
                      {TYPE_LABELS[h.intervention_type] ?? h.intervention_type}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {h.delivered_at ? (
                        <span className="text-emerald-600 font-medium">
                          Yes
                        </span>
                      ) : (
                        <span className="text-theme-muted">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-theme-muted">
                      {h.delivered_by ?? "—"}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {h.outcome_30d ?? "—"}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {h.outcome_60d ?? "—"}
                    </td>
                    <td className="py-2 pl-3 text-xs">
                      {h.outcome_90d ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
